import type { CreateInkColorInput } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useInkColorsQuery, useInvalidateInkColors } from '@/entities/ink-color';
import {
  InkColorFormModal,
  InkColorRow,
  useCreateInkColor,
  useDeleteInkColor,
  useRenameInkColor,
} from '@/features/ink-colors';
import { type RenameTarget, resetSettledRequests } from '@/shared/lib';
import { Button, ContentCard, cn, EmptyState, tableStyles } from '@/shared/ui';

/** 삭제 영향 — 적용 상품 수를 목록에 싣지 않으므로(결정 F4) 확인창이 영향을 알리는 유일한 자리다. */
const DELETE_IMPACT =
  '이 잉크를 허용한 모든 상품에서 빠집니다. 기존 디자인의 글자 색은 그대로지만, 이 잉크를 쓴 디자인은 다른 잉크로 바꿔야 다시 저장할 수 있습니다.';

/**
 * 잉크 색상 마스터 — 고객 에디터의 글자 색 목록(견본·이름·색상 코드·관리). 폰트 마스터와 대칭이다.
 * 이름 변경·삭제는 모든 상품의 허용 목록에 보이므로 상품별 허용 목록 캐시까지 무효화한다.
 */
export const InkColorMasterSection = (): ReactElement => {
  const invalidate = useInvalidateInkColors();
  const inkColorsQuery = useInkColorsQuery();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);

  const createAction = useCreateInkColor({ onSuccess: invalidate.list });
  const renameAction = useRenameInkColor({
    onSuccess: () => {
      invalidate.afterMasterChange();
      setRenameTarget(null);
    },
  });
  const deleteAction = useDeleteInkColor({ onSuccess: invalidate.afterMasterChange });

  const mutationError = renameAction.error ?? deleteAction.error;

  // 이름 수정·삭제는 오류 한 줄(mutationError)을 함께 쓴다(추가 오류는 추가 모달이 보여 준다). 새 요청을
  // 보내기 전에 두 요청의 지난 오류를 모두 지운다. 진행 중인 요청은 지우지 않는다 — 인쇄 방식 마스터와 같은 이유.
  const clearMutationErrors = (): void => {
    resetSettledRequests([renameAction, deleteAction]);
  };

  // 모달을 열고 닫을 때 지난 등록 오류를 지운다 — 새 모달에 옛 오류가 남지 않게.
  const changeAddModal = (isOpen: boolean): void => {
    resetSettledRequests([createAction]);
    setIsAddModalOpen(isOpen);
  };

  const handleCreate = async (input: CreateInkColorInput): Promise<void> => {
    if (await createAction.createInkColor(input)) {
      changeAddModal(false);
    }
  };

  const rename = {
    target: renameTarget
      ? { ...renameTarget, isPending: renameAction.pendingId === renameTarget.id }
      : null,
    onChange: (name: string) =>
      setRenameTarget((current) => (current ? { ...current, name } : current)),
    onSubmit: () => {
      if (renameTarget?.name.trim()) {
        clearMutationErrors();
        renameAction.renameInkColor({ id: renameTarget.id, name: renameTarget.name.trim() });
      }
    },
    onCancel: () => setRenameTarget(null),
  };

  const handleDelete = (inkColor: { readonly id: string; readonly name: string }): void => {
    if (
      window.confirm(`잉크 색상 "${inkColor.name}" 을(를) 삭제하시겠습니까?\n\n${DELETE_IMPACT}`)
    ) {
      clearMutationErrors();
      deleteAction.deleteInkColor({ id: inkColor.id });
    }
  };

  return (
    <ContentCard
      title="잉크 색상"
      description="고객 에디터에서 글자에 쓰는 잉크 색입니다. 상품마다 허용할 색은 상품 편집에서 고릅니다."
      actions={
        <Button variant="outline" size="sm" onClick={() => changeAddModal(true)}>
          + 잉크 색상 추가
        </Button>
      }
    >
      {mutationError ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {mutationError.message}
        </p>
      ) : null}

      {inkColorsQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : inkColorsQuery.isError ? (
        <EmptyState isError>
          잉크 색상을 불러오지 못했습니다: {inkColorsQuery.error.message}
        </EmptyState>
      ) : inkColorsQuery.data.length === 0 ? (
        <EmptyState>
          등록된 잉크 색상이 없습니다. 잉크 색상을 추가해야 상품에서 허용할 수 있습니다.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
          <table className={tableStyles.table}>
            <thead className={tableStyles.head}>
              <tr>
                <th className={cn(tableStyles.th, 'w-20')}>견본</th>
                <th className={tableStyles.th}>잉크 색상명</th>
                <th className={cn(tableStyles.th, 'w-40')}>색상 코드</th>
                <th className={cn(tableStyles.th, 'w-48')}>관리</th>
              </tr>
            </thead>
            <tbody>
              {inkColorsQuery.data.map((inkColor) => (
                <InkColorRow
                  key={inkColor.id}
                  inkColor={inkColor}
                  rename={rename}
                  onRenameStart={(target) => setRenameTarget({ id: target.id, name: target.name })}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        ⓘ 색상 코드는 등록한 뒤 바꿀 수 없습니다 — 이름만 수정할 수 있습니다.
      </p>

      {isAddModalOpen ? (
        <InkColorFormModal
          isPending={createAction.isPending}
          errorMessage={createAction.error}
          nameErrorMessage={createAction.nameError}
          onSubmit={(input) => void handleCreate(input)}
          onClose={() => changeAddModal(false)}
        />
      ) : null}
    </ContentCard>
  );
};
